import { useMemo } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useIxcMutualfundsV1MutualFundsTaskQueryServiceMutualFundsTaskGet } from '@interxfi/api-client/generated/hooks/ixc-mutualfunds-v1-mutual-funds-task-query-service-mutual-funds-task-get';
import { EntityActionMenu, type EntityActionMenuConfig } from '@interxfi/app-components/components/entity-action-menu/entity-action-menu';
import { PageHeader } from '@interxfi/app-components/components/layout/page-header/page-header';
import {
  DEFAULT_PAGE_LAYOUT_GAP,
  DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE,
  PageLayout,
} from '@interxfi/app-components/components/layout/page-layout/page-layout';
import { AppReadonlyList } from '@interxfi/app-components/components/readonly-list/app-readonly-list';
import { BoxedAppReadonlyList } from '@interxfi/app-components/components/readonly-list/boxed-app-readonly-list';
import { type AggregateBreadcrumb, buildBreadcrumb } from '@interxfi/app-components/components/router-breadcrumbs/router-breadcrumbs';
import { HorizontalStack } from '@interxfi/design-system/components/alignment/horizontal-stack';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { CopyButton } from '@interxfi/design-system/components/copy-button/copy-button';
import { LAYOUT_ZERO_CONTAINER_PADDING } from '@interxfi/design-system/components/layout-container/layout-container';
import {
  DEFAULT_WRAPPING_LAYOUT_COLUMN,
  WrappingLayoutContainer,
} from '@interxfi/design-system/components/layout-container/wrapping/wrapping-layout-container';
import type { ReadonlyListItem } from '@interxfi/design-system/components/list';
import { Typography } from '@interxfi/design-system/components/typography';
import type { IxcMutualfundsV1MutualFundsTaskState } from '@interxfi/types';
import {
  MutualFundsTaskEventDrawer,
  MutualFundsTaskEventSummary,
} from '../../../../../../../../../../features/mutual-funds/task/mutual-funds-task-event';
import { getMutualFundsTaskStateListItems } from '../../../../../../../../../../features/mutual-funds/task/mutual-funds-task-list-items';
import { getMutualFundsTaskDetailPath } from '../../../../../../../../../../features/mutual-funds/task/mutual-funds-task-routing';
import { MutualFundsTaskStatusPill } from '../../../../../../../../../../features/mutual-funds/task/mutual-funds-task-status-pill';
import { buildDefaultErrorMessageGetter, useErrorHandler } from '../../../../../../../../../../util/error-handler';
import { getPageHead } from '../../../../../../../../../../util/routing/meta';

export const Route = createFileRoute('/_app/_clearing-core/_clearing-required/clearing/operations/_operations/mutualfunds/task/_task/$taskId')({
  loader: ({ params }) =>
    ({
      breadcrumb: buildBreadcrumb({
        id: `mutual-funds-task-${params.taskId}`,
        label: 'Task',
        to: getMutualFundsTaskDetailPath(params.taskId),
        suffix: (
          <CopyButton
            content={params.taskId}
            label="Copy Mutual Funds Task ID"
            tooltip={{ copy: params.taskId, copied: 'Copied Mutual Funds Task ID' }}
          />
        ),
      }),
    }) satisfies AggregateBreadcrumb,
  head: () => getPageHead('Mutual Funds Task'),
  component: MutualFundsTaskDetail,
});

function getListItems(data: IxcMutualfundsV1MutualFundsTaskState | undefined) {
  const listItems = getMutualFundsTaskStateListItems(data);
  const details = listItems.data.spec.dividendsPay.details;

  return {
    left: [
      listItems.data.operationType,
      listItems.data.spec.dividendsPay.endDate,
      ...(details ? [details.amountPaid, details.executedAt, ...(details.transactionId ? [details.transactionId] : [])] : []),
    ] as const satisfies ReadonlyListItem[],
    right: [
      listItems.metadata.createdAt,
      listItems.metadata.updatedAt,
      listItems.mutualFundsTaskId,
      listItems.mutualFundsJobId,
      listItems.accountId,
      listItems.assetId,
    ] as const satisfies ReadonlyListItem[],
  };
}

function getMenuItems(mutualFundsTaskId: string): EntityActionMenuConfig {
  return {
    eventDrawer: {
      type: 'drawer',
      drawer: {
        Component: MutualFundsTaskEventDrawer,
        props: { request: { mutualFundsTaskId } },
      },
    },
    // TODO: wire command useIxcMutualfundsV1MutualFundsTaskCommandServiceTaskRecover
    actions: undefined,
  };
}

const errorMessageGetter = buildDefaultErrorMessageGetter('Failed to load mutual funds task', 'view this mutual funds task');

function MutualFundsTaskDetail() {
  const { taskId } = Route.useParams();
  const request = { mutualFundsTaskId: taskId } as const;
  const { data, error, isPending } = useIxcMutualfundsV1MutualFundsTaskQueryServiceMutualFundsTaskGet(request);
  useErrorHandler(error, errorMessageGetter);
  const showPending = Boolean(isPending || error);
  const listItems = useMemo(() => getListItems(data?.mutualFundsTask), [data?.mutualFundsTask]);
  const entityMenuItems = useMemo(() => getMenuItems(taskId), [taskId]);

  return (
    <PageLayout gap={DEFAULT_PAGE_LAYOUT_GAP} maxWidthContainer={DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE}>
      <PageLayout.Section>
        <VerticalStack gap={7}>
          <PageHeader>
            <HorizontalStack gap={2} wrap="nowrap">
              <MutualFundsTaskStatusPill isPending={showPending} size="large" status={data?.mutualFundsTask?.status} />
            </HorizontalStack>

            <EntityActionMenu {...entityMenuItems} isPending={showPending} />
          </PageHeader>

          <VerticalStack gap={4}>
            <Typography.Level3>Events</Typography.Level3>

            <MutualFundsTaskEventSummary gap={DEFAULT_PAGE_LAYOUT_GAP} padding={LAYOUT_ZERO_CONTAINER_PADDING} request={request} />
          </VerticalStack>
        </VerticalStack>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Data</Typography.Level3>

          <WrappingLayoutContainer
            gap={DEFAULT_PAGE_LAYOUT_GAP}
            left={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <AppReadonlyList isPending={showPending} items={listItems.left} />,
            }}
            padding={LAYOUT_ZERO_CONTAINER_PADDING}
            right={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <BoxedAppReadonlyList isPending={showPending} items={listItems.right} />,
            }}
            wrap="forward"
            wrapGap={7}
          />
        </VerticalStack>
      </PageLayout.Section>
    </PageLayout>
  );
}
